import type { ReactNode } from 'react'

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { CircleAlert, CircleCheck, Info } from 'lucide-react'
import { cn } from '@/lib/utils'

type Tone = 'info' | 'success' | 'error'

type Toast = {
    id: number
    message: string
    tone: Tone
}

type ToastContextValue = {
    notify: (message: string, tone?: Tone) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const ICONS = {
    info: Info,
    success: CircleCheck,
    error: CircleAlert
}

interface ToastProviderProps {
    children: ReactNode
}

export const ToastProvider = ({ children }: ToastProviderProps): ReactNode => {
    const [toasts, setToasts] = useState<Array<Toast>>([])
    const counter = useRef(0)

    const notify = useCallback((message: string, tone: Tone = 'info'): void => {
        counter.current += 1

        const id = counter.current

        setToasts(previous => [...previous.slice(-2), { id, message, tone }])
        window.setTimeout(() => setToasts(previous => previous.filter(toast => toast.id !== id)), 3800)
    }, [])

    const value = useMemo(() => ({ notify }), [notify])

    return (
        <ToastContext.Provider value={value}>
            {children}

            <div
                aria-live='polite'
                role='status'
                className='pointer-events-none fixed inset-x-0 bottom-24 z-[120] flex flex-col items-center gap-2 px-4 sm:bottom-8'
            >
                {toasts.map(toast => {
                    const Icon = ICONS[toast.tone]

                    return (
                        <div
                            key={toast.id}
                            className={cn(
                                'pointer-events-auto flex max-w-sm items-center gap-2.5 rounded-2xl border bg-popover px-4 py-3 text-sm text-popover-foreground shadow-lg shadow-black/10',
                                'animate-in fade-in slide-in-from-bottom-2 duration-300'
                            )}
                        >
                            <Icon
                                aria-hidden='true'
                                className={cn(
                                    'size-4 shrink-0',
                                    toast.tone === 'success' && 'text-emerald-500',
                                    toast.tone === 'error' && 'text-destructive',
                                    toast.tone === 'info' && 'text-primary'
                                )}
                            />
                            <span>
                                {toast.message}
                            </span>
                        </div>
                    )
                })}
            </div>
        </ToastContext.Provider>
    )
}

export const useToast = (): ToastContextValue => {
    const context = useContext(ToastContext)

    if (!context) throw new Error('useToast must be used inside ToastProvider.')

    return context
}
